import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Key, Eye, EyeOff, Save, Loader2, CheckCircle2, AlertTriangle, CreditCard, Sparkles, Bot, Search, Zap } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white font-mono placeholder:text-[#555] focus:outline-none focus:border-[#0055FF] transition-colors";

const ApiKeysSettings = ({ token }) => {
  const [keys, setKeys] = useState(null);
  const [form, setForm] = useState({
    stripe_api_key: '',
    resend_api_key: '',
    google_api_key: '',
    sender_email: '',
    anthropic_api_key: '',
    bing_azure_client_id: '',
    bing_azure_tenant_id: '',
    bing_azure_client_secret: ''
  });
  const [visible, setVisible] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState({});  // { anthropic: {ok, message}, bing_azure: {...} }
  const [testing, setTesting] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/api-keys`, { headers });
      setKeys(res.data);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = {};
      const fieldNames = [
        'stripe_api_key', 'resend_api_key', 'google_api_key', 'sender_email',
        'anthropic_api_key',
        'bing_azure_client_id', 'bing_azure_tenant_id', 'bing_azure_client_secret'
      ];
      fieldNames.forEach(f => { if (form[f] && form[f].trim()) payload[f] = form[f].trim(); });

      if (Object.keys(payload).length === 0) {
        setError('Inserisci almeno una chiave da aggiornare.');
        setSaving(false);
        return;
      }

      await axios.put(`${API_URL}/api/admin/api-keys`, payload, { headers });
      setSaved(true);
      setForm({
        stripe_api_key: '', resend_api_key: '', google_api_key: '', sender_email: '',
        anthropic_api_key: '',
        bing_azure_client_id: '', bing_azure_tenant_id: '', bing_azure_client_secret: ''
      });
      fetchKeys();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    setTesting(t => ({ ...t, [provider]: true }));
    setTestResults(r => ({ ...r, [provider]: null }));
    try {
      const endpoint = provider === 'anthropic' ? 'test-anthropic' : 'test-bing-azure';
      const res = await axios.post(`${API_URL}/api/admin/api-keys/${endpoint}`, {}, { headers });
      setTestResults(r => ({ ...r, [provider]: res.data }));
    } catch (err) {
      setTestResults(r => ({ ...r, [provider]: { ok: false, error: err.response?.data?.detail || err.message } }));
    } finally {
      setTesting(t => ({ ...t, [provider]: false }));
    }
  };

  const toggleVisible = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));

  const keyConfigs = [
    {
      id: 'stripe_api_key',
      label: 'Stripe API Key',
      icon: <CreditCard className="w-5 h-5 text-[#6366F1]" />,
      placeholder: 'sk_live_...',
      description: 'Chiave segreta di Stripe per pagamenti reali e Customer Portal.',
      help: 'Ottienila da dashboard.stripe.com > Developers > API Keys'
    },
    {
      id: 'resend_api_key',
      label: 'Resend API Key',
      icon: <svg className="w-5 h-5 text-[#00C4B4]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="m22 6-10 7L2 6"/></svg>,
      placeholder: 're_...',
      description: 'Per inviare email transazionali (inviti, reset password, notifiche).',
      help: 'Ottienila da resend.com > API Keys'
    },
    {
      id: 'google_api_key',
      label: 'Google API Key (Gemini)',
      icon: <Sparkles className="w-5 h-5 text-[#F59E0B]" />,
      placeholder: 'AIzaSy...',
      description: 'Chiave Google per gli agenti AI (Gemini 2.5 Flash). Usata per generare contenuti SEO/AEO.',
      help: 'Ottienila da aistudio.google.com/apikey'
    },
    {
      id: 'sender_email',
      label: 'Sender Email',
      icon: <svg className="w-5 h-5 text-[#A1A1AA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="m22 6-10 7L2 6"/></svg>,
      placeholder: 'noreply@tuodominio.com',
      description: 'Indirizzo email mittente per le email transazionali.',
      help: 'Deve essere verificato nel tuo account Resend'
    },
    {
      id: 'anthropic_api_key',
      label: 'Anthropic API Key',
      icon: <Bot className="w-5 h-5 text-[#D97757]" />,
      placeholder: 'sk-ant-api03-...',
      description: "Chiave Anthropic per gli agenti AI Premium+ (claude-haiku-4-5) e Premium++ (claude-sonnet-4-7). Usata per generare contenuti SEO/AEO di qualità superiore.",
      help: 'Ottienila da console.anthropic.com > API Keys'
    }
  ];

  return (
    <div className="bg-[#111111] border border-white/10 rounded-2xl p-6" data-testid="api-keys-settings">
      <div className="flex items-center gap-3 mb-6">
        <Key className="w-6 h-6 text-[#0055FF]" />
        <h4 className="font-['Outfit'] font-medium text-lg">Chiavi API</h4>
        {saved && (
          <span className="flex items-center gap-1 text-xs text-[#10B981]">
            <CheckCircle2 className="w-3.5 h-3.5" /> Salvato
          </span>
        )}
      </div>

      <div className="space-y-5">
        {keyConfigs.map(({ id, label, icon, placeholder, description, help }) => {
          const keyData = keys?.[id];
          const isConfigured = keyData?.configured;
          const maskedValue = keyData?.value_masked || keyData?.value || '';

          return (
            <div key={id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {icon}
                  <span className="text-sm font-medium">{label}</span>
                  {isConfigured ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#10B981]/20 text-[#10B981]">Attivo</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#F59E0B]/20 text-[#F59E0B]">Non configurato</span>
                  )}
                  {id === 'stripe_api_key' && keyData?.is_test && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#F59E0B]/20 text-[#F59E0B]">Test Key</span>
                  )}
                </div>
                {maskedValue && (
                  <code className="text-[10px] text-[#A1A1AA] font-mono">{maskedValue}</code>
                )}
              </div>
              <p className="text-[11px] text-[#555]">{description}</p>
              <div className="relative">
                <input
                  type={visible[id] ? 'text' : 'password'}
                  className={inputCls}
                  placeholder={placeholder}
                  value={form[id] || ''}
                  onChange={(e) => setForm({ ...form, [id]: e.target.value })}
                  data-testid={`api-key-input-${id}`}
                />
                <button
                  onClick={() => toggleVisible(id)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                  data-testid={`toggle-visibility-${id}`}
                >
                  {visible[id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-[#A1A1AA]">{help}</p>
              {id === 'anthropic_api_key' && keys?.anthropic_api_key?.configured && (
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    type="button"
                    onClick={() => handleTest('anthropic')}
                    disabled={testing.anthropic}
                    className="h-7 px-2 text-[11px] bg-white/5 border border-white/10 hover:bg-white/10"
                    data-testid="test-anthropic-btn"
                  >
                    {testing.anthropic ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                    Test connessione
                  </Button>
                  {testResults.anthropic && (
                    <span className={`text-[11px] ${testResults.anthropic.ok ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      {testResults.anthropic.ok ? `✓ ${testResults.anthropic.model || 'OK'}` : `✗ ${testResults.anthropic.error || 'Failed'}`}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Microsoft Azure (Bing Webmaster) — 3 nested fields + redirect URI */}
        <div className="pt-5 mt-5 border-t border-white/10 space-y-3" data-testid="bing-azure-section">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-[#0078D4]" />
            <span className="text-sm font-medium">Microsoft Azure (Bing Webmaster Tools)</span>
            {keys?.bing_azure?.client_id?.configured && keys?.bing_azure?.tenant_id?.configured && keys?.bing_azure?.client_secret?.configured ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#10B981]/20 text-[#10B981]">Attivo</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#F59E0B]/20 text-[#F59E0B]">Non configurato</span>
            )}
          </div>
          <p className="text-[11px] text-[#555]">
            OAuth customer-side e citation tracking ChatGPT/Copilot/Bing Chat. Vedi documentazione .85-beta.
          </p>

          {[
            { key: 'client_id',     label: 'Application (Client) ID',  field: 'bing_azure_client_id',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
            { key: 'tenant_id',     label: 'Directory (Tenant) ID',    field: 'bing_azure_tenant_id',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
            { key: 'client_secret', label: 'Client Secret',            field: 'bing_azure_client_secret', type: 'password', placeholder: 'xxx~yyy.zzz' }
          ].map(({ key, label, field, type, placeholder }) => {
            const data = keys?.bing_azure?.[key];
            return (
              <div key={field} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#A1A1AA]">{label}</span>
                  {data?.value_masked && <code className="text-[10px] text-[#A1A1AA] font-mono">{data.value_masked}</code>}
                </div>
                <div className="relative">
                  <input
                    type={type === 'password' ? (visible[field] ? 'text' : 'password') : 'text'}
                    className={inputCls}
                    placeholder={placeholder}
                    value={form[field] || ''}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    data-testid={`bing-input-${key}`}
                  />
                  {type === 'password' && (
                    <button
                      type="button"
                      onClick={() => toggleVisible(field)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                    >
                      {visible[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="space-y-1">
            <span className="text-xs text-[#A1A1AA]">Redirect URI (read-only)</span>
            <code className="block w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-[#A1A1AA] font-mono">
              {keys?.bing_azure?.redirect_uri || 'https://aeo-orchestra.com/oauth/bing/callback'}
            </code>
          </div>

          <p className="text-[10px] text-[#A1A1AA]">
            Ottienila da portal.azure.com &gt; Microsoft Entra ID &gt; App registrations &gt; AEO Orchestra Bing Connector
          </p>

          {keys?.bing_azure?.tenant_id?.configured && (
            <div className="flex items-center gap-2 pt-1">
              <Button
                type="button"
                onClick={() => handleTest('bing_azure')}
                disabled={testing.bing_azure}
                className="h-7 px-2 text-[11px] bg-white/5 border border-white/10 hover:bg-white/10"
                data-testid="test-bing-btn"
              >
                {testing.bing_azure ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                Test connessione
              </Button>
              {testResults.bing_azure && (
                <span className={`text-[11px] ${testResults.bing_azure.ok ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {testResults.bing_azure.ok ? `✓ Tenant valido` : `✗ ${testResults.bing_azure.error || 'Failed'}`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
          <span className="text-xs text-[#EF4444]">{error}</span>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <p className="text-[10px] text-[#555]">
          Le chiavi vengono salvate in modo sicuro e applicate immediatamente senza riavvio.
        </p>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0055FF] hover:bg-[#3377FF]"
          data-testid="save-api-keys-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Salva Chiavi
        </Button>
      </div>
    </div>
  );
};

export default ApiKeysSettings;
